import type { UserFileRead } from '@/uppy-file-upload/schemas'
import type { FileUploadState } from '@/uppy-file-upload/store/slice'
import { UserFileProcessingStatus } from '@/user_files/schemas/processing-status'

export type UserFileAttachmentStatus = 'uploading' | 'processing' | 'ready' | 'error'

export type UserFileAttachmentStatusResult = {
  status: UserFileAttachmentStatus
  errorMessage?: string
}

export function getUserFileAttachmentStatus(params: {
  file?: UserFileRead
  uploadState?: FileUploadState
  loadError?: boolean
}): UserFileAttachmentStatusResult {
  const { file, uploadState, loadError } = params
  const clientStatus = uploadState?.client.uploadStatus

  if (clientStatus === 'unsupported_type') {
    return { status: 'error', errorMessage: 'Unsupported file type' }
  }

  if (clientStatus === 'error') {
    return { status: 'error', errorMessage: uploadState?.client.uploadError ?? 'Upload failed' }
  }

  if (clientStatus === 'validating' || clientStatus === 'queued' || clientStatus === 'initializing' || clientStatus === 'uploading') {
    return { status: 'uploading' }
  }

  const record = uploadState?.backend?.record ?? file

  if (clientStatus === 'uploaded' && !record) {
    return { status: 'processing' }
  }

  if (loadError && !record) {
    return { status: 'error', errorMessage: 'Could not load file' }
  }

  if (!record) {
    return { status: 'processing' }
  }

  if (record?.processing_status === UserFileProcessingStatus.Failed) {
    return { status: 'error', errorMessage: record.processing_error ?? 'Processing failed' }
  }

  if (record?.processing_status === UserFileProcessingStatus.Pending || record?.processing_status === UserFileProcessingStatus.Processing) {
    return { status: 'processing' }
  }

  return { status: 'ready' }
}
